/**
 * Enterprise-grade debugging utility for Mermaid diagram processing.
 *
 * Provides comprehensive logging, validation testing, and error analysis
 * for diagnosing complex Mermaid syntax issues.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import type { Logger } from './Logger.js';

/**
 * Debugging information for a diagram
 */
export interface DiagramDebugInfo {
  readonly index: number;
  readonly originalLength: number;
  readonly processedLength: number;
  readonly hasProblematicPatterns: boolean;
  readonly detectedIssues: string[];
  readonly originalContent: string;
  readonly processedContent: string;
}

/**
 * Comprehensive debugging utility for diagram processing
 */
export class DiagramDebugger {
  private readonly logger: Logger;
  private readonly debugOutputDir: string;

  constructor(logger: Logger) {
    this.logger = logger;
    this.debugOutputDir = path.join(process.cwd(), 'debug-output');
    this.ensureDebugDirectory();
  }

  /**
   * Debug and analyze a diagram thoroughly
   */
  async debugDiagram(
    diagramCode: string,
    index: number,
    processedCode?: string
  ): Promise<DiagramDebugInfo> {
    const timestamp = Date.now();

    this.logger.info(`[DiagramDebugger] Starting debug analysis for diagram ${index}`, {
      originalLength: diagramCode.length,
      timestamp,
    });

    // Analyze problematic patterns
    const detectedIssues = this.analyzeProblematicPatterns(diagramCode);

    // Save debug files for inspection
    await this.saveDebugFiles(diagramCode, processedCode, index, timestamp);

    // Log detailed analysis
    this.logDetailedAnalysis(diagramCode, processedCode, index, detectedIssues);

    const debugInfo: DiagramDebugInfo = {
      index,
      originalLength: diagramCode.length,
      processedLength: processedCode?.length || diagramCode.length,
      hasProblematicPatterns: detectedIssues.length > 0,
      detectedIssues,
      originalContent: diagramCode,
      processedContent: processedCode || diagramCode,
    };

    return debugInfo;
  }

  /**
   * Analyze diagram for known problematic patterns
   */
  private analyzeProblematicPatterns(code: string): string[] {
    const issues: string[] = [];

    // Check for currency symbols
    if (/[¢$€£¥]/.test(code)) {
      issues.push('Contains currency symbols that may break parsing');
    }

    // Check for problematic parentheses in labels
    if (/O\(n\)/gi.test(code)) {
      issues.push('Contains O(n) notation that may break node parsing');
    }

    if (/Start\s*\(\s*Stack:\s*Z\s*\)/.test(code)) {
      issues.push('Contains Stack notation with parentheses');
    }

    // Check for complex state transitions
    if (/note\s+right\s+of\s+\w+:\s*State:\s*[^;]*[¢$€£¥]/.test(code)) {
      issues.push('State diagram notes contain currency symbols');
    }

    // Check for quote issues
    if (/'\('.*?'\)'/.test(code)) {
      issues.push('Contains quoted parentheses in transitions');
    }

    // Check for unbalanced quotes
    const singleQuotes = (code.match(/'/g) || []).length;
    const doubleQuotes = (code.match(/"/g) || []).length;

    if (singleQuotes % 2 !== 0) {
      issues.push(`Unbalanced single quotes (count: ${singleQuotes})`);
    }

    if (doubleQuotes % 2 !== 0) {
      issues.push(`Unbalanced double quotes (count: ${doubleQuotes})`);
    }

    // Check for HTML entities
    if (/&[a-zA-Z0-9#]+;/.test(code)) {
      issues.push('Contains HTML entities that need decoding');
    }

    return issues;
  }

  /**
   * Log detailed analysis of diagram processing
   */
  private logDetailedAnalysis(
    originalCode: string,
    processedCode: string | undefined,
    index: number,
    detectedIssues: string[]
  ): void {
    this.logger.debug(`[DiagramDebugger] Diagram ${index} analysis:`, {
      originalLength: originalCode.length,
      processedLength: processedCode?.length || 'N/A',
      issueCount: detectedIssues.length,
      issues: detectedIssues,
    });

    // Log first 100 characters of original for inspection
    const preview = originalCode.substring(0, 100).replace(/\n/g, '\\n');
    this.logger.debug(`[DiagramDebugger] Diagram ${index} preview: "${preview}..."`);

    if (processedCode && processedCode !== originalCode) {
      const processedPreview = processedCode.substring(0, 100).replace(/\n/g, '\\n');
      this.logger.debug(`[DiagramDebugger] Diagram ${index} processed: "${processedPreview}..."`);
    }
  }

  /**
   * Save debug files for manual inspection
   */
  private async saveDebugFiles(
    originalCode: string,
    processedCode: string | undefined,
    index: number,
    timestamp: number
  ): Promise<void> {
    try {
      const baseFilename = `diagram-${index}-${timestamp}`;

      // Save original diagram
      const originalFile = path.join(this.debugOutputDir, `${baseFilename}-original.mmd`);
      fs.writeFileSync(originalFile, originalCode, 'utf-8');

      // Save processed diagram if different
      if (processedCode && processedCode !== originalCode) {
        const processedFile = path.join(this.debugOutputDir, `${baseFilename}-processed.mmd`);
        fs.writeFileSync(processedFile, processedCode, 'utf-8');
      }

      // Save analysis report
      const analysisFile = path.join(this.debugOutputDir, `${baseFilename}-analysis.txt`);
      const issues = this.analyzeProblematicPatterns(originalCode);
      const analysis = [
        `Diagram ${index} Analysis Report`,
        `Generated: ${new Date(timestamp).toISOString()}`,
        `Original Length: ${originalCode.length}`,
        `Processed Length: ${processedCode?.length || 'N/A'}`,
        `Issues Found: ${issues.length}`,
        '',
        'Detected Issues:',
        ...issues.map((issue) => `- ${issue}`),
        '',
        'Original Content:',
        originalCode,
        ...(processedCode && processedCode !== originalCode
          ? ['', 'Processed Content:', processedCode]
          : []),
      ].join('\n');

      fs.writeFileSync(analysisFile, analysis, 'utf-8');

      this.logger.debug(`[DiagramDebugger] Debug files saved for diagram ${index}`, {
        originalFile,
        processedFile: processedCode ? `${baseFilename}-processed.mmd` : 'N/A',
        analysisFile,
      });
    } catch (error) {
      this.logger.warn(`[DiagramDebugger] Failed to save debug files: ${(error as Error).message}`);
    }
  }

  /**
   * Test specific patterns against sample text
   */
  testPattern(
    pattern: RegExp,
    replacement: string,
    testText: string
  ): {
    matches: boolean;
    originalText: string;
    replacedText: string;
    matchCount: number;
  } {
    const matches = pattern.test(testText);
    const replacedText = testText.replace(pattern, replacement);
    const matchCount = (testText.match(pattern) || []).length;

    this.logger.debug('[DiagramDebugger] Pattern test result', {
      pattern: pattern.toString(),
      replacement,
      matches,
      matchCount,
      changed: testText !== replacedText,
    });

    return {
      matches,
      originalText: testText,
      replacedText,
      matchCount,
    };
  }

  /**
   * Create comprehensive debug report for all diagrams
   */
  async generateDebugReport(debugInfos: DiagramDebugInfo[]): Promise<void> {
    const timestamp = Date.now();
    const reportFile = path.join(this.debugOutputDir, `debug-report-${timestamp}.md`);

    const totalDiagrams = debugInfos.length;
    const problematicDiagrams = debugInfos.filter((info) => info.hasProblematicPatterns).length;

    const report = [
      '# Mermaid Diagram Debug Report',
      `Generated: ${new Date(timestamp).toISOString()}`,
      '',
      '## Summary',
      `Total Diagrams: ${totalDiagrams}`,
      `Problematic Diagrams: ${problematicDiagrams}`,
      `Success Rate: ${(((totalDiagrams - problematicDiagrams) / totalDiagrams) * 100).toFixed(1)}%`,
      '',
      '## Detailed Analysis',
      ...debugInfos.map((info) =>
        [
          `### Diagram ${info.index}`,
          `Original Length: ${info.originalLength}`,
          `Processed Length: ${info.processedLength}`,
          `Has Issues: ${info.hasProblematicPatterns}`,
          `Issues: ${info.detectedIssues.length}`,
          ...info.detectedIssues.map((issue) => `- ${issue}`),
          '',
        ].join('\n')
      ),
    ].join('\n');

    fs.writeFileSync(reportFile, report, 'utf-8');

    this.logger.info(`[DiagramDebugger] Debug report generated: ${reportFile}`, {
      totalDiagrams,
      problematicDiagrams,
      successRate: `${(((totalDiagrams - problematicDiagrams) / totalDiagrams) * 100).toFixed(1)}%`,
    });
  }

  /**
   * Ensure debug output directory exists
   */
  private ensureDebugDirectory(): void {
    try {
      if (!fs.existsSync(this.debugOutputDir)) {
        fs.mkdirSync(this.debugOutputDir, { recursive: true });
        this.logger.debug(`[DiagramDebugger] Created debug directory: ${this.debugOutputDir}`);
      }
    } catch (error) {
      this.logger.warn(
        `[DiagramDebugger] Failed to create debug directory: ${(error as Error).message}`
      );
    }
  }
}
